import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Package,
  Loader2,
  CheckCircle2,
  Search,
  Shirt,
  GraduationCap,
  AlertTriangle,
  CalendarDays,
  User,
  Wrench,
  PackageCheck,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  History,
  ScanBarcode,
  ImageOff,
  X,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { devolutionService } from "@/features/devolutions/services/devolution.service";
import { cleanDocument } from "@/shared/lib/document";
import { formatDocument } from "@/shared/lib/formatters";
import type {
  RentalDetailResponseDTO,
  DevolutionGownReturnItemDTO,
  StockItemFlatDTO,
} from "@/features/devolutions/types/devolution.types";

// ─── Types ────────────────────────────────────────────────────────────────────

type RentalTab = "SUIT" | "GOWN" | "HISTORY";

// ─── Helpers de acesso seguro ─────────────────────────────────────────────────

function getRentalId(r: RentalDetailResponseDTO): number {
  return r.idRental ?? r.id ?? 0;
}

function getRentalType(r: RentalDetailResponseDTO): string {
  return r.rentalType ?? r.type ?? "";
}

function getContractNumber(r: RentalDetailResponseDTO): string {
  return r.contract?.contractNumber ?? r.contractNumber ?? "—";
}

function getClientName(r: RentalDetailResponseDTO): string {
  return r.contract?.client?.name ?? r.client?.name ?? r.clientName ?? "—";
}

function getClientDocument(r: RentalDetailResponseDTO): string {
  const raw =
    r.contract?.client?.document ??
    r.client?.document ??
    r.clientDocument ??
    "";
  return raw ? formatDocument(raw) : "—";
}

function getSellerName(r: RentalDetailResponseDTO): string {
  return r.seller?.name ?? r.sellerName ?? "—";
}

function getTotalValue(r: RentalDetailResponseDTO): number {
  return r.totalValue ?? 0;
}

function getStockItems(r: RentalDetailResponseDTO): StockItemFlatDTO[] {
  return r.stockItems ?? [];
}

function getGownItems(r: RentalDetailResponseDTO) {
  return r.gownItems ?? [];
}

function getPickupDate(r: RentalDetailResponseDTO): string | null | undefined {
  return r.outAt ?? r.expectedOutAt ?? r.rentedAt;
}

function getReturnDate(r: RentalDetailResponseDTO): string | null | undefined {
  return r.inAt ?? r.expectedInAt;
}

// ─── Lookup tables ────────────────────────────────────────────────────────────

const RENTAL_STATUS_LABELS: Record<string, string> = {
  INCOMPLETE: "Incompleto",
  COMPLETE: "Completo",
  ON_STREET: "Em rua",
  CANCELED: "Cancelado",
  CANCELLED: "Cancelado",
  FINISHED: "Finalizado",
  RETURNED: "Devolvido",
};

const CONDITION_LABELS: Record<string, string> = {
  NEW: "Novo",
  USED: "Usado",
  WORN_OUT: "Desgastado",
  GOOD: "Bom",
  REGULAR: "Regular",
  POOR: "Ruim",
};

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  JACKET: "Paletó",
  PANTS: "Calça",
  VEST: "Colete",
  SHIRT: "Camisa",
  TIE: "Gravata",
  BOW_TIE: "Gravata Borboleta",
  SUSPENDER: "Suspensório",
  CUFFLINKS: "Abotoaduras",
  SHOES: "Sapatos",
  GOWN: "Beca",
  HAT: "Chapéu",
  HOOD: "Capelo",
  STOLE: "Estola",
  SUIT: "Terno",
};

const RENTAL_TYPE_BADGES: Record<string, { label: string; className: string }> =
  {
    SUIT: {
      label: "Terno",
      className: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    },
    GOWN: {
      label: "Beca",
      className: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    },
  };

// ─── Utils ────────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("pt-BR");
}

function buildSearchParams(
  contrato: string,
  documento: string,
  nome: string,
  tab: RentalTab,
  page: number,
  size: number,
  contratoId: number | null = null,
) {
  const params: Record<string, unknown> = { page, size };
  if (tab === "HISTORY") {
    params.statuses = ["FINISHED"];
    params.sortBy = "inAt";
    params.sortDirection = "DESC";
  } else if (tab === "GOWN") {
    params.statuses = ["ON_STREET"];
    params.rentalTypes = ["GOWN"];
  } else {
    params.statuses = ["ON_STREET"];
    params.rentalTypes = ["SUIT"];
  }
  if (contratoId != null) {
    params.contractIds = [contratoId];
  } else if (contrato.trim()) {
    params.contractNumber = contrato.trim();
  }
  if (documento.trim()) params.clientDocument = documento.trim();
  if (nome.trim()) params.clientName = nome.trim();
  return params;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function DevolucaoPage() {
  const [activeTab, setActiveTab] = useState<RentalTab>("SUIT");
  const [searchContrato, setSearchContrato] = useState("");
  const [searchContratoId, setSearchContratoId] = useState<number | null>(null);
  const [searchDocumento, setSearchDocumento] = useState("");
  const [searchNome, setSearchNome] = useState("");
  const [listRentals, setListRentals] = useState<RentalDetailResponseDTO[]>([]);
  const [listLoading, setListLoading] = useState(false);

  const [rental, setRental] = useState<RentalDetailResponseDTO | null>(null);
  const [returnedItemIds, setReturnedItemIds] = useState<number[]>([]);
  const [returnedShoesPairs, setReturnedShoesPairs] = useState<number[]>([]);
  const [maintenanceDays, setMaintenanceDays] = useState(1);
  const [gownItems, setGownItems] = useState<DevolutionGownReturnItemDTO[]>([]);
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnError, setReturnError] = useState<string | null>(null);
  const [invoiceObservation, setInvoiceObservation] = useState("");
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showPartialDialog, setShowPartialDialog] = useState(false);

  const [quickSearch, setQuickSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [historyRentals, setHistoryRentals] = useState<
    RentalDetailResponseDTO[]
  >([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyQuickSearch, setHistoryQuickSearch] = useState("");
  const [historyCurrentPage, setHistoryCurrentPage] = useState(0);
  const [historyPageSize] = useState(5);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [historyTotalElements, setHistoryTotalElements] = useState(0);

  const [suggestions, setSuggestions] = useState<RentalDetailResponseDTO[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<
    "contrato" | "documento" | "nome" | null
  >(null);

  const [expandedPhotoUrl, setExpandedPhotoUrl] = useState<string | null>(null);
  const [barcodeInput, setBarcodeInput] = useState("");
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoOpenedRef = useRef(false);
  const contratoRef = useRef<HTMLDivElement>(null);
  const documentoRef = useRef<HTMLDivElement>(null);
  const nomeRef = useRef<HTMLDivElement>(null);

  async function loadRentals(
    contrato: string,
    documento: string,
    nome: string,
    tab: RentalTab,
    page = 0,
    size = 5,
    contratoId: number | null = null,
  ) {
    const params = buildSearchParams(
      contrato,
      documento,
      nome,
      tab,
      page,
      size,
      contratoId,
    );

    if (tab === "HISTORY") {
      setHistoryLoading(true);
      try {
        const result = await devolutionService.searchRentals(
          params as Parameters<typeof devolutionService.searchRentals>[0],
        );
        setHistoryRentals(result.content);
        setHistoryCurrentPage(result.page);
        setHistoryTotalPages(result.totalPages);
        setHistoryTotalElements(result.totalElements);
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error, "Erro ao carregar histórico."));
      } finally {
        setHistoryLoading(false);
      }
      return;
    }

    setListLoading(true);
    try {
      const result = await devolutionService.searchRentals(
        params as Parameters<typeof devolutionService.searchRentals>[0],
      );
      let content = result.content;
      if (tab === "SUIT")
        content = content.filter((r) => getRentalType(r) !== "GOWN");
      const sorted = [...content].sort((a, b) => {
        const da = a.outAt ?? a.expectedOutAt ?? a.rentedAt ?? "";
        const db = b.outAt ?? b.expectedOutAt ?? b.rentedAt ?? "";
        return da.localeCompare(db);
      });
      setListRentals(sorted);
      setCurrentPage(result.page);
      setTotalPages(result.totalPages);
      setTotalElements(result.totalElements);
    } catch (error: unknown) {
      toast.error(
        getApiErrorMessage(error, "Erro ao carregar contratos em aberto."),
      );
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    // Reset de estado local ao trocar de tab. Sincronização com activeTab.
    // Padrão legado; migrar quando DevolucaoPage for refatorada.
    /* eslint-disable react-hooks/set-state-in-effect */
    setCurrentPage(0);
    setQuickSearch("");
    setHistoryCurrentPage(0);
    setHistoryQuickSearch("");
    /* eslint-enable react-hooks/set-state-in-effect */
    if (activeTab === "HISTORY") {
      loadRentals("", "", "", "HISTORY", 0, historyPageSize);
    } else {
      loadRentals("", "", "", activeTab, 0, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (autoOpenedRef.current) return;
    const idParam = searchParams.get("rentalId");
    if (!idParam) return;
    const id = Number(idParam);
    if (!id) return;
    autoOpenedRef.current = true;
    void devolutionService
      .getById(id)
      .then((r) => {
        selectRental(r);
      })
      .catch((error: unknown) => {
        toast.error(
          getApiErrorMessage(error, "Não foi possível carregar o aluguel."),
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const refs = [contratoRef, documentoRef, nomeRef];
      if (
        refs.every((r) => r.current && !r.current.contains(e.target as Node))
      ) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleTabChange(tab: RentalTab) {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setActiveTab(tab);
    setSearchContrato("");
    setSearchContratoId(null);
    setSearchDocumento("");
    setSearchNome("");
    setSuggestions([]);
    setActiveDropdown(null);
    setRental(null);
    resetForm();
  }

  function handleSearchFieldChange(
    field: "contrato" | "documento" | "nome",
    value: string,
  ) {
    if (field === "contrato") {
      setSearchContrato(value);
      setSearchContratoId(null);
    } else if (field === "documento") setSearchDocumento(value);
    else setSearchNome(value);

    setActiveDropdown(field);

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    if (!value.trim()) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    setSuggestionsLoading(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const base: Record<string, unknown> = { page: 0, size: 8 };
        if (activeTab === "HISTORY") {
          base.statuses = ["FINISHED"];
        } else if (activeTab === "GOWN") {
          base.statuses = ["ON_STREET"];
          base.rentalTypes = ["GOWN"];
        } else {
          base.statuses = ["ON_STREET"];
        }
        const params =
          field === "contrato"
            ? { ...base, contractNumber: value.trim() }
            : field === "documento"
              ? { ...base, clientDocument: value.trim() }
              : { ...base, clientName: value.trim() };
        const result = await devolutionService.searchRentals(
          params as Parameters<typeof devolutionService.searchRentals>[0],
        );
        let items = result.content;
        if (activeTab === "SUIT")
          items = items.filter((r) => getRentalType(r) !== "GOWN");
        setSuggestions(items);
      } catch {
        setSuggestions([]);
      } finally {
        setSuggestionsLoading(false);
      }
    }, 350);
  }

  function selectSuggestion(
    field: "contrato" | "documento" | "nome",
    r: RentalDetailResponseDTO,
  ) {
    if (field === "contrato") {
      setSearchContrato(getContractNumber(r));
      setSearchContratoId(
        r.contractId ?? r.contract?.idContract ?? r.contract?.id ?? null,
      );
    } else if (field === "documento") {
      setSearchDocumento(
        r.clientDocument ?? r.contract?.client?.document ?? "",
      );
    } else {
      setSearchNome(getClientName(r));
    }
    setSuggestions([]);
    setActiveDropdown(null);
  }

  function handleBuscar() {
    setSuggestions([]);
    setActiveDropdown(null);
    if (activeTab === "HISTORY") {
      setHistoryCurrentPage(0);
      setHistoryQuickSearch("");
      loadRentals(
        searchContrato,
        searchDocumento,
        searchNome,
        "HISTORY",
        0,
        historyPageSize,
        searchContratoId,
      );
    } else {
      setCurrentPage(0);
      setQuickSearch("");
      loadRentals(
        searchContrato,
        searchDocumento,
        searchNome,
        activeTab,
        0,
        pageSize,
        searchContratoId,
      );
    }
  }

  function handleLimparFiltros() {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchContrato("");
    setSearchContratoId(null);
    setSearchDocumento("");
    setSearchNome("");
    setSuggestions([]);
    setActiveDropdown(null);
    if (activeTab === "HISTORY") {
      setHistoryCurrentPage(0);
      setHistoryQuickSearch("");
      loadRentals("", "", "", "HISTORY", 0, historyPageSize, null);
    } else {
      setCurrentPage(0);
      setQuickSearch("");
      loadRentals("", "", "", activeTab, 0, pageSize, null);
    }
  }

  function handlePageChange(page: number) {
    setCurrentPage(page);
    loadRentals(
      searchContrato,
      searchDocumento,
      searchNome,
      activeTab,
      page,
      pageSize,
      searchContratoId,
    );
  }

  function handleHistoryPageChange(page: number) {
    setHistoryCurrentPage(page);
    loadRentals(
      searchContrato,
      searchDocumento,
      searchNome,
      "HISTORY",
      page,
      historyPageSize,
      searchContratoId,
    );
  }

  function resetForm() {
    setMaintenanceDays(1);
    setReturnedItemIds([]);
    setReturnedShoesPairs([]);
    setGownItems([]);
    setReturnError(null);
    setInvoiceObservation("");
    setBarcodeInput("");
  }

  function handleBarcodeSubmit(code: string) {
    const trimmed = code.trim();
    if (!trimmed || !rental) return;
    const items = getStockItems(rental);
    const match = items.find(
      (s) => s.barcode.toLowerCase() === trimmed.toLowerCase(),
    );
    if (!match) {
      toast.error(`Código "${trimmed}" não encontrado nos itens do aluguel.`);
      setBarcodeInput("");
      return;
    }
    if (returnedItemIds.includes(match.id)) {
      toast.info(`${match.productName} já está marcado como devolvido.`);
      setBarcodeInput("");
      return;
    }
    setReturnedItemIds((prev) => [...prev, match.id]);
    toast.success(`${match.productName} marcado como devolvido.`);
    setBarcodeInput("");
  }

  function selectRental(r: RentalDetailResponseDTO) {
    setRental(r);
    resetForm();
    const alreadyReturnedIds = getStockItems(r)
      .filter((s) => s.returnedAt)
      .map((s) => s.id);
    if (alreadyReturnedIds.length > 0) {
      setReturnedItemIds(alreadyReturnedIds);
    }
    if (getRentalType(r) === "GOWN") {
      setMaintenanceDays(0);
      const items = getGownItems(r);
      if (items.length > 0) {
        setGownItems(
          items.map((item) => ({
            productId:
              item.productId ??
              item.product?.idProduct ??
              item.product?.id ??
              0,
            quantity: 0,
          })),
        );
      }
    }
    setShowReturnModal(true);
  }

  function handleModalClose() {
    setShowReturnModal(false);
    setShowPartialDialog(false);
    setRental(null);
    resetForm();
    if (activeTab === "HISTORY") {
      loadRentals(
        searchContrato,
        searchDocumento,
        searchNome,
        "HISTORY",
        historyCurrentPage,
        historyPageSize,
        searchContratoId,
      );
    } else {
      loadRentals(
        searchContrato,
        searchDocumento,
        searchNome,
        activeTab,
        currentPage,
        pageSize,
        searchContratoId,
      );
    }
  }

  function updateGownItemQuantity(index: number, value: number) {
    setGownItems((prev) =>
      prev.map((g, i) =>
        i === index ? { ...g, quantity: Math.max(0, value) } : g,
      ),
    );
  }

  function allProductsReturned(): boolean {
    if (!rental) return false;
    const type = getRentalType(rental);
    if (type === "SUIT") {
      const items = getStockItems(rental);
      const stocksOk =
        items.length === 0 ||
        items.every((s) => returnedItemIds.includes(s.id));
      const pairCount = rental.hasShoes
        ? Math.max(1, rental.shoesPairQuantity ?? 1)
        : 0;
      const shoesOk =
        !rental.hasShoes || returnedShoesPairs.length === pairCount;
      return stocksOk && shoesOk;
    } else {
      const rentalItems = getGownItems(rental);
      if (rentalItems.length === 0) return true;
      const allAlreadyReturned = rentalItems.every((ri) => {
        const remaining = Math.max(
          0,
          (ri.quantity ?? 0) - (ri.returnedQuantity ?? 0),
        );
        return remaining === 0;
      });
      if (allAlreadyReturned) return true;
      const hasAnyPreviousReturn = rentalItems.some(
        (ri) => (ri.returnedQuantity ?? 0) > 0,
      );
      if (hasAnyPreviousReturn) return true;
      return gownItems.some((item) => item.quantity > 0);
    }
  }

  function validateForm(): boolean {
    if (!rental) return false;

    if (!allProductsReturned()) {
      if (getRentalType(rental) === "GOWN") {
        toast.error(
          "Informe a quantidade devolvida de pelo menos um item antes de confirmar.",
        );
      } else {
        toast.error(
          "Marque todos os produtos como devolvidos antes de confirmar a devolução.",
        );
      }
      return false;
    }

    if (maintenanceDays < 0) {
      toast.error("Dias de manutenção deve ser maior ou igual a 0.");
      return false;
    }

    return true;
  }

  function isPartialReturn(): boolean {
    if (!rental || getRentalType(rental) !== "GOWN") return false;
    const rentalGownItems = getGownItems(rental);
    return gownItems.some((item, index) => {
      const ri = rentalGownItems[index];
      if (!ri) return false;
      const remaining = Math.max(
        0,
        (ri.quantity ?? 0) - (ri.returnedQuantity ?? 0),
      );
      return item.quantity > 0 && item.quantity < remaining;
    });
  }

  function handleConfirmClick() {
    if (!validateForm()) return;
    if (isPartialReturn()) {
      setShowPartialDialog(true);
    } else {
      void handleReturn();
    }
  }

  async function handleReturn() {
    if (!rental || returnLoading) return;

    const rentalId = getRentalId(rental);
    setReturnLoading(true);
    setReturnError(null);

    try {
      let partialWarning: string | null = null;

      try {
        const isGown = getRentalType(rental) === "GOWN";
        const trimmedObservation = invoiceObservation.trim();
        await devolutionService.returnRental(rentalId, {
          ...(isGown ? {} : { maintenanceDays }),
          payments: [],
          stockIds: getStockItems(rental).map((s) => s.id),
          gownItems,
          ...(trimmedObservation
            ? { invoiceObservation: trimmedObservation }
            : {}),
        });
      } catch (err) {
        const apiErr = err as {
          response?: {
            status?: number;
            data?: { error?: string; message?: string };
          };
        };
        const data = apiErr.response?.data;
        if (
          apiErr.response?.status === 400 &&
          data?.error === "BUSINESS_ERROR"
        ) {
          partialWarning = data?.message ?? "Devolução parcial registrada.";
        } else {
          throw err;
        }
      }

      // Fechar modal e atualizar lista
      setShowReturnModal(false);
      setShowPartialDialog(false);
      setRental(null);
      resetForm();
      if (activeTab === "HISTORY") {
        loadRentals(
          searchContrato,
          searchDocumento,
          searchNome,
          "HISTORY",
          historyCurrentPage,
          historyPageSize,
          searchContratoId,
        );
      } else {
        loadRentals(
          searchContrato,
          searchDocumento,
          searchNome,
          activeTab,
          currentPage,
          pageSize,
          searchContratoId,
        );
      }

      if (partialWarning) {
        toast.warning(partialWarning);
      } else {
        toast.success("Devolução processada com sucesso!");
      }
    } catch (error: unknown) {
      const message = getApiErrorMessage(
        error,
        "Erro ao processar devolução. Verifique os dados e tente novamente.",
      );
      setReturnError(message);
      toast.error(message);
    } finally {
      setReturnLoading(false);
    }
  }

  // ─── Computed values ───────────────────────────────────────────────────────

  const filteredRentals = quickSearch.trim()
    ? listRentals.filter((r) => {
        const q = quickSearch.toLowerCase();
        return (
          getContractNumber(r).toLowerCase().includes(q) ||
          getClientName(r).toLowerCase().includes(q) ||
          cleanDocument(getClientDocument(r)).includes(
            cleanDocument(quickSearch),
          ) ||
          getSellerName(r).toLowerCase().includes(q)
        );
      })
    : listRentals;

  const filteredHistory = historyQuickSearch.trim()
    ? historyRentals.filter((r) => {
        const q = historyQuickSearch.toLowerCase();
        return (
          getContractNumber(r).toLowerCase().includes(q) ||
          getClientName(r).toLowerCase().includes(q) ||
          cleanDocument(getClientDocument(r)).includes(
            cleanDocument(historyQuickSearch),
          ) ||
          getSellerName(r).toLowerCase().includes(q) ||
          getRentalType(r).toLowerCase().includes(q)
        );
      })
    : historyRentals;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          Devolução de Aluguel
        </h1>
        <p className="text-muted-foreground">
          Processe devoluções e atualize o estoque
        </p>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <div className="flex border-b border-border">
        {(
          [
            { key: "SUIT", label: "Ternos", Icon: Shirt },
            { key: "GOWN", label: "Becas", Icon: GraduationCap },
            { key: "HISTORY", label: "Histórico de Devoluções", Icon: History },
          ] as const
        ).map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => handleTabChange(key)}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Search ──────────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Filtrar Contratos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Contrato */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Número do Contrato
              </label>
              <div ref={contratoRef} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none z-10" />
                <Input
                  placeholder="Ex.: 2024-001"
                  value={searchContrato}
                  onChange={(e) =>
                    handleSearchFieldChange("contrato", e.target.value)
                  }
                  onFocus={() => {
                    if (suggestions.length > 0) setActiveDropdown("contrato");
                  }}
                  className="pl-8"
                />
                {activeDropdown === "contrato" &&
                  (suggestionsLoading || suggestions.length > 0) && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-background shadow-md">
                      {suggestionsLoading ? (
                        <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Buscando...
                        </div>
                      ) : (
                        <ul className="max-h-48 overflow-y-auto py-1">
                          {suggestions.map((r, i) => (
                            <li key={getRentalId(r) || i}>
                              <button
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  selectSuggestion("contrato", r);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                              >
                                <span className="font-medium">
                                  {getContractNumber(r)}
                                </span>
                                <span className="ml-2 text-xs text-muted-foreground">
                                  {getClientName(r)}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
              </div>
            </div>

            {/* Documento */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Documento (CPF / CNPJ)
              </label>
              <div ref={documentoRef} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none z-10" />
                <Input
                  placeholder="000.000.000-00"
                  value={searchDocumento}
                  onChange={(e) =>
                    handleSearchFieldChange("documento", e.target.value)
                  }
                  onFocus={() => {
                    if (suggestions.length > 0) setActiveDropdown("documento");
                  }}
                  className="pl-8"
                />
                {activeDropdown === "documento" &&
                  (suggestionsLoading || suggestions.length > 0) && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-background shadow-md">
                      {suggestionsLoading ? (
                        <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Buscando...
                        </div>
                      ) : (
                        <ul className="max-h-48 overflow-y-auto py-1">
                          {suggestions.map((r, i) => (
                            <li key={getRentalId(r) || i}>
                              <button
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  selectSuggestion("documento", r);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                              >
                                <span className="font-medium font-mono text-xs">
                                  {getClientDocument(r)}
                                </span>
                                <span className="ml-2 text-xs text-muted-foreground">
                                  {getClientName(r)}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
              </div>
            </div>

            {/* Nome */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Nome do Cliente
              </label>
              <div ref={nomeRef} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none z-10" />
                <Input
                  placeholder="Nome completo ou parcial"
                  value={searchNome}
                  onChange={(e) =>
                    handleSearchFieldChange("nome", e.target.value)
                  }
                  onFocus={() => {
                    if (suggestions.length > 0) setActiveDropdown("nome");
                  }}
                  className="pl-8"
                />
                {activeDropdown === "nome" &&
                  (suggestionsLoading || suggestions.length > 0) && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-background shadow-md">
                      {suggestionsLoading ? (
                        <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Buscando...
                        </div>
                      ) : (
                        <ul className="max-h-48 overflow-y-auto py-1">
                          {suggestions.map((r, i) => (
                            <li key={getRentalId(r) || i}>
                              <button
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  selectSuggestion("nome", r);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                              >
                                <span className="font-medium">
                                  {getClientName(r)}
                                </span>
                                <span className="ml-2 text-xs text-muted-foreground font-mono">
                                  {getClientDocument(r)}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={handleBuscar}
              disabled={listLoading || historyLoading}
              className="gap-2"
            >
              {listLoading || historyLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Buscar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleLimparFiltros}
              disabled={listLoading || historyLoading}
            >
              Limpar filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Contract list ────────────────────────────────────────────────────── */}
      {activeTab !== "HISTORY" && (
        <Card>
          <CardHeader className="pb-3 space-y-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Contratos em aberto</CardTitle>
              {!listLoading && totalElements > 0 && (
                <span className="text-xs text-muted-foreground px-2.5 py-1 rounded-full bg-muted/50 border border-border">
                  {totalElements} contrato{totalElements !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-end gap-2">
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Busca rápida..."
                  value={quickSearch}
                  onChange={(e) => setQuickSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  loadRentals(
                    searchContrato,
                    searchDocumento,
                    searchNome,
                    activeTab,
                    currentPage,
                    pageSize,
                    searchContratoId,
                  )
                }
                disabled={listLoading}
                className="h-8 px-2.5"
                title="Atualizar lista"
              >
                <RefreshCw
                  className={`w-3.5 h-3.5 ${listLoading ? "animate-spin" : ""}`}
                />
              </Button>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0 || listLoading}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <span className="text-xs text-muted-foreground px-1 tabular-nums whitespace-nowrap">
                  {totalPages > 0 ? `${currentPage + 1} / ${totalPages}` : "—"}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1 || listLoading}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {listLoading && listRentals.length === 0 ? (
              <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Carregando contratos...</span>
              </div>
            ) : filteredRentals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                <div className="w-14 h-14 rounded-full bg-muted/30 flex items-center justify-center">
                  <Package className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {quickSearch.trim()
                      ? "Nenhum resultado para a busca rápida"
                      : (searchContrato || searchDocumento || searchNome).trim()
                        ? "Nenhum contrato encontrado"
                        : "Nenhum contrato em aberto"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {quickSearch.trim()
                      ? "Tente outros termos ou limpe a busca rápida."
                      : (searchContrato || searchDocumento || searchNome).trim()
                        ? "Tente ajustar os filtros de busca."
                        : "Todos os contratos estão quitados ou não há registros."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="pb-3 px-6 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Contrato
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Cliente
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden md:table-cell">
                        Documento
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden lg:table-cell">
                        Vendedor
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden md:table-cell">
                        Retirada
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide text-right">
                        Total
                      </th>
                      <th className="pb-3 px-6 text-xs font-medium text-muted-foreground uppercase tracking-wide text-right">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRentals.map((r, index) => {
                      return (
                        <tr
                          key={getRentalId(r) || index}
                          className="border-b border-border last:border-0 hover:bg-accent/50 transition-colors"
                        >
                          <td className="py-4 px-6">
                            <span className="font-semibold text-foreground">
                              {getContractNumber(r)}
                            </span>
                          </td>
                          <td className="py-4 px-3 font-medium">
                            {getClientName(r)}
                          </td>
                          <td className="py-4 px-3 font-mono text-xs text-muted-foreground hidden md:table-cell">
                            {getClientDocument(r)}
                          </td>
                          <td className="py-4 px-3 text-muted-foreground hidden lg:table-cell">
                            {getSellerName(r)}
                          </td>
                          <td className="py-4 px-3 text-muted-foreground hidden md:table-cell">
                            {formatDate(r.outAt)}
                          </td>
                          <td className="py-4 px-3 text-right font-semibold tabular-nums">
                            {formatCurrency(getTotalValue(r))}
                          </td>
                          <td className="py-4 px-6 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => selectRental(r)}
                              title="Devolver"
                            >
                              <PackageCheck className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── History list ─────────────────────────────────────────────────────── */}
      {activeTab === "HISTORY" && (
        <Card>
          <CardHeader className="pb-3 space-y-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Devoluções finalizadas
              </CardTitle>
              {!historyLoading && historyTotalElements > 0 && (
                <span className="text-xs text-muted-foreground px-2.5 py-1 rounded-full bg-muted/50 border border-border">
                  {historyTotalElements} registro
                  {historyTotalElements !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-end gap-2">
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Busca rápida..."
                  value={historyQuickSearch}
                  onChange={(e) => setHistoryQuickSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  loadRentals(
                    searchContrato,
                    searchDocumento,
                    searchNome,
                    "HISTORY",
                    historyCurrentPage,
                    historyPageSize,
                    searchContratoId,
                  )
                }
                disabled={historyLoading}
                className="h-8 px-2.5"
                title="Atualizar lista"
              >
                <RefreshCw
                  className={`w-3.5 h-3.5 ${historyLoading ? "animate-spin" : ""}`}
                />
              </Button>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleHistoryPageChange(historyCurrentPage - 1)
                  }
                  disabled={historyCurrentPage === 0 || historyLoading}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <span className="text-xs text-muted-foreground px-1 tabular-nums whitespace-nowrap">
                  {historyTotalPages > 0
                    ? `${historyCurrentPage + 1} / ${historyTotalPages}`
                    : "—"}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleHistoryPageChange(historyCurrentPage + 1)
                  }
                  disabled={
                    historyCurrentPage >= historyTotalPages - 1 ||
                    historyLoading
                  }
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {historyLoading && historyRentals.length === 0 ? (
              <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Carregando histórico...</span>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                <div className="w-14 h-14 rounded-full bg-muted/30 flex items-center justify-center">
                  <History className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {historyQuickSearch.trim()
                      ? "Nenhum resultado para a busca rápida"
                      : (searchContrato || searchDocumento || searchNome).trim()
                        ? "Nenhum registro encontrado"
                        : "Nenhuma devolução finalizada"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {historyQuickSearch.trim()
                      ? "Tente outros termos ou limpe a busca rápida."
                      : (searchContrato || searchDocumento || searchNome).trim()
                        ? "Tente ajustar os filtros de busca."
                        : "Não há devoluções finalizadas no sistema."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="pb-3 px-6 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Foto
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Tipo
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Contrato
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Cliente
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden md:table-cell">
                        Documento
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden lg:table-cell">
                        Vendedor
                      </th>
                      <th className="pb-3 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden md:table-cell">
                        Devolução
                      </th>
                      <th className="pb-3 px-6 text-xs font-medium text-muted-foreground uppercase tracking-wide text-right">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.map((r, index) => {
                      const type = getRentalType(r);
                      const badge = RENTAL_TYPE_BADGES[type] ?? {
                        label: type,
                        className:
                          "bg-muted/50 text-muted-foreground border-border",
                      };
                      const firstPhoto = getStockItems(r).find(
                        (s) => s.photoUrl,
                      )?.photoUrl;
                      return (
                        <tr
                          key={getRentalId(r) || index}
                          className="border-b border-border last:border-0 transition-colors"
                        >
                          <td className="py-3 px-6">
                            {firstPhoto ? (
                              <button
                                type="button"
                                onClick={() => setExpandedPhotoUrl(firstPhoto)}
                                className="block w-10 h-10 rounded-md overflow-hidden border border-border hover:border-primary hover:opacity-80 transition-all shrink-0"
                                title="Ampliar foto"
                              >
                                <img
                                  src={firstPhoto}
                                  alt="Produto"
                                  className="w-full h-full object-cover"
                                />
                              </button>
                            ) : (
                              <div className="w-10 h-10 rounded-md border border-border bg-muted/30 flex items-center justify-center">
                                <ImageOff className="w-4 h-4 text-muted-foreground/50" />
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                          </td>
                          <td className="py-3 px-3 font-semibold text-foreground">
                            {getContractNumber(r)}
                          </td>
                          <td className="py-3 px-3 font-medium">
                            {getClientName(r)}
                          </td>
                          <td className="py-3 px-3 font-mono text-xs text-muted-foreground hidden md:table-cell">
                            {getClientDocument(r)}
                          </td>
                          <td className="py-3 px-3 text-muted-foreground hidden lg:table-cell">
                            {getSellerName(r)}
                          </td>
                          <td className="py-3 px-3 text-muted-foreground hidden md:table-cell">
                            {formatDate(getReturnDate(r))}
                          </td>
                          <td className="py-3 px-6 text-right font-semibold tabular-nums">
                            {formatCurrency(getTotalValue(r))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Return modal ──────────────────────────────────────────────────────── */}
      <Dialog
        open={showReturnModal}
        onOpenChange={(open) => {
          if (!open) handleModalClose();
        }}
      >
        {rental && (
          <DialogContent className="max-w-2xl">
            <DialogHeader className="pb-4 border-b border-border">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                  {getRentalType(rental) === "SUIT"
                    ? "Aluguel de Terno"
                    : "Aluguel de Beca"}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  {RENTAL_STATUS_LABELS[rental.status] ?? rental.status}
                </span>
              </div>
              <DialogTitle className="text-xl">
                Contrato {getContractNumber(rental)}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Formulário de devolução para o contrato{" "}
                {getContractNumber(rental)}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              {/* Info grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-xl border border-border bg-muted/20 p-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Cliente
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-foreground">
                    {getClientName(rental)}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {getClientDocument(rental)}
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Vendedor
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-foreground">
                    {getSellerName(rental)}
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Data da retirada
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-foreground">
                    {formatDate(getPickupDate(rental))}
                  </p>
                </div>
              </div>

              {/* ── Seção 1: Produtos ──────────────────────────────────── */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 pb-3 border-b border-border">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 border border-primary/25 shrink-0">
                    <Package className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">
                      Itens para devolução
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {getRentalType(rental) === "GOWN"
                        ? "Informe a quantidade de cada item devolvido"
                        : "Marque todos os itens conforme forem sendo devolvidos"}
                    </p>
                  </div>
                  {getRentalType(rental) === "SUIT" &&
                    (() => {
                      const pairCount = rental.hasShoes
                        ? Math.max(1, rental.shoesPairQuantity ?? 1)
                        : 0;
                      const total = getStockItems(rental).length + pairCount;
                      const returned =
                        returnedItemIds.length + returnedShoesPairs.length;
                      return total > 0 ? (
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full border shrink-0 font-medium tabular-nums ${
                            allProductsReturned()
                              ? "bg-green-500/10 text-green-400 border-green-500/20"
                              : "bg-muted/50 text-muted-foreground border-border"
                          }`}
                        >
                          {returned}/{total}
                        </span>
                      ) : null;
                    })()}
                </div>

                {getRentalType(rental) === "SUIT" ? (
                  getStockItems(rental).length === 0 && !rental.hasShoes ? (
                    <p className="text-sm text-muted-foreground py-2">
                      Nenhuma peça vinculada a este aluguel.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {/* Barcode / code scan input */}
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                          <Input
                            ref={barcodeInputRef}
                            placeholder="Bipagem ou código do produto — Enter para confirmar"
                            value={barcodeInput}
                            onChange={(e) => setBarcodeInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleBarcodeSubmit(barcodeInput);
                              }
                            }}
                            className="pl-10"
                            autoComplete="off"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleBarcodeSubmit(barcodeInput)}
                          disabled={!barcodeInput.trim()}
                          className="shrink-0"
                        >
                          Marcar
                        </Button>
                      </div>

                      <div className="space-y-2">
                        {getStockItems(rental).map((stock) => {
                          const checked = returnedItemIds.includes(stock.id);
                          const alreadyReturned = !!stock.returnedAt;
                          return (
                            <label
                              key={stock.id}
                              className={`flex items-center gap-3 rounded-lg border p-3 transition-all ${
                                alreadyReturned
                                  ? "border-green-500/40 bg-green-500/5 cursor-default opacity-70"
                                  : checked
                                    ? "border-green-500/40 bg-green-500/5 cursor-pointer"
                                    : "border-border hover:bg-accent/50 cursor-pointer"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={alreadyReturned}
                                onChange={() => {
                                  if (alreadyReturned) return;
                                  setReturnedItemIds((prev) =>
                                    prev.includes(stock.id)
                                      ? prev.filter((x) => x !== stock.id)
                                      : [...prev, stock.id],
                                  );
                                }}
                                className="w-4 h-4 accent-primary shrink-0"
                              />
                              {stock.photoUrl ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setExpandedPhotoUrl(stock.photoUrl!);
                                  }}
                                  className="w-10 h-10 rounded-md overflow-hidden border border-border hover:border-primary hover:opacity-80 transition-all shrink-0"
                                  title="Ampliar foto"
                                >
                                  <img
                                    src={stock.photoUrl}
                                    alt={stock.productName}
                                    className="w-full h-full object-cover"
                                  />
                                </button>
                              ) : (
                                <div className="w-10 h-10 rounded-md border border-border bg-muted/30 flex items-center justify-center shrink-0">
                                  <ImageOff className="w-4 h-4 text-muted-foreground/40" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground text-sm">
                                  {stock.productName}
                                  {stock.productType && (
                                    <span className="font-normal text-muted-foreground ml-2 text-xs">
                                      {PRODUCT_TYPE_LABELS[stock.productType] ??
                                        stock.productType}
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {[
                                    stock.productBrand,
                                    stock.productColor,
                                    stock.size ? `Tam. ${stock.size}` : null,
                                    stock.barcode
                                      ? `Cód. ${stock.barcode}`
                                      : null,
                                    CONDITION_LABELS[stock.condition] ??
                                      stock.condition,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </p>
                              </div>
                              {alreadyReturned ? (
                                <span className="text-xs text-green-400 font-medium shrink-0 flex items-center gap-1">
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                  Já devolvido
                                </span>
                              ) : checked ? (
                                <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                              ) : null}
                            </label>
                          );
                        })}

                        {rental.hasShoes &&
                          Array.from(
                            {
                              length: Math.max(
                                1,
                                rental.shoesPairQuantity ?? 1,
                              ),
                            },
                            (_, i) => {
                              const checked = returnedShoesPairs.includes(i);
                              return (
                                <label
                                  key={`shoes-${i}`}
                                  className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-all ${
                                    checked
                                      ? "border-green-500/40 bg-green-500/5"
                                      : "border-border hover:bg-accent/50"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                      setReturnedShoesPairs((prev) =>
                                        prev.includes(i)
                                          ? prev.filter((x) => x !== i)
                                          : [...prev, i],
                                      )
                                    }
                                    className="w-4 h-4 accent-primary cursor-pointer shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-foreground text-sm">
                                      Sapatos
                                      {(rental.shoesPairQuantity ?? 1) > 1 && (
                                        <span className="font-normal text-muted-foreground ml-2 text-xs">
                                          Par {i + 1}
                                        </span>
                                      )}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      Incluído no aluguel
                                    </p>
                                  </div>
                                  {checked && (
                                    <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                                  )}
                                </label>
                              );
                            },
                          )}
                      </div>
                    </div>
                  )
                ) : getGownItems(rental).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">
                    Nenhum item de beca vinculado a este aluguel.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {gownItems.map((item, index) => {
                      const rentalItem = getGownItems(rental)[index];
                      const totalQty = rentalItem?.quantity ?? 0;
                      const alreadyReturned = rentalItem?.returnedQuantity ?? 0;
                      const remaining = Math.max(0, totalQty - alreadyReturned);
                      const totalAfter = alreadyReturned + item.quantity;
                      const isComplete = totalAfter === totalQty;
                      return (
                        <div
                          key={index}
                          className={`rounded-lg border p-4 space-y-4 transition-colors ${
                            item.quantity > 0
                              ? isComplete
                                ? "border-green-500/30 bg-green-500/5"
                                : "border-yellow-500/30 bg-yellow-500/5"
                              : "border-border bg-muted/10"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-2">
                              <p className="font-semibold text-foreground text-sm">
                                Beca
                              </p>
                              <div className="flex flex-wrap gap-3">
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">
                                    Total
                                  </p>
                                  <p className="text-sm font-bold tabular-nums">
                                    {totalQty}
                                  </p>
                                </div>
                                {alreadyReturned > 0 && (
                                  <div className="text-center">
                                    <p className="text-xs text-muted-foreground">
                                      Devolvido
                                    </p>
                                    <p className="text-sm font-bold text-green-400 tabular-nums">
                                      {alreadyReturned}
                                    </p>
                                  </div>
                                )}
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">
                                    Restante
                                  </p>
                                  <p className="text-sm font-bold text-muted-foreground tabular-nums">
                                    {remaining}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="shrink-0 space-y-1.5">
                              <p className="text-xs text-muted-foreground text-center">
                                Devolvendo agora
                              </p>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateGownItemQuantity(
                                      index,
                                      item.quantity - 1,
                                    )
                                  }
                                  disabled={item.quantity === 0}
                                  className="w-8 h-8 flex items-center justify-center rounded-md border border-input hover:bg-accent transition-colors text-base font-medium disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  −
                                </button>
                                <Input
                                  type="number"
                                  min={0}
                                  max={remaining}
                                  value={
                                    item.quantity === 0 ? "" : item.quantity
                                  }
                                  placeholder="0"
                                  onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    updateGownItemQuantity(
                                      index,
                                      isNaN(v)
                                        ? 0
                                        : Math.min(remaining, Math.max(0, v)),
                                    );
                                  }}
                                  className="w-16 text-center font-bold text-base px-1"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateGownItemQuantity(
                                      index,
                                      item.quantity + 1,
                                    )
                                  }
                                  disabled={item.quantity >= remaining}
                                  className="w-8 h-8 flex items-center justify-center rounded-md border border-input hover:bg-accent transition-colors text-base font-medium disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>

                          {item.quantity > 0 && (
                            <div className="flex items-center gap-2 pt-3 border-t border-border/50">
                              {isComplete ? (
                                <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />
                              )}
                              <p className="text-xs">
                                <span className="font-semibold">
                                  {totalAfter} de {totalQty}
                                </span>
                                <span className="text-muted-foreground ml-1">
                                  {isComplete
                                    ? "— devolução completa"
                                    : `— parcial, restam ${totalQty - totalAfter}`}
                                </span>
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Seção 2: Manutenção (apenas Ternos) ───────────────── */}
              {getRentalType(rental) !== "GOWN" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 pb-3 border-b border-border">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 border border-primary/25 shrink-0">
                      <Wrench className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Dias de manutenção
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Período em que os itens ficam indisponíveis após a
                        devolução
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setMaintenanceDays(Math.max(0, maintenanceDays - 1))
                        }
                        disabled={maintenanceDays === 0}
                        className="w-8 h-8 flex items-center justify-center rounded-md border border-input hover:bg-accent transition-colors text-base font-medium disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        −
                      </button>
                      <Input
                        id="maintenanceDays"
                        type="number"
                        min={0}
                        value={maintenanceDays}
                        onChange={(e) =>
                          setMaintenanceDays(
                            Math.max(0, parseInt(e.target.value, 10) || 0),
                          )
                        }
                        className="w-20 text-center font-bold text-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setMaintenanceDays(maintenanceDays + 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-md border border-input hover:bg-accent transition-colors text-base font-medium"
                      >
                        +
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {maintenanceDays === 0
                        ? "Sem manutenção"
                        : maintenanceDays === 1
                          ? "1 dia de manutenção"
                          : `${maintenanceDays} dias de manutenção`}
                    </p>
                  </div>
                </div>
              )}

              {/* Observação da NF */}
              <div className="space-y-2">
                <label
                  htmlFor="devolucao-invoice-observation"
                  className="text-sm font-medium"
                >
                  Observação da NF
                </label>
                <Textarea
                  id="devolucao-invoice-observation"
                  placeholder="Descrição que aparece na nota fiscal"
                  rows={3}
                  maxLength={2000}
                  value={invoiceObservation}
                  onChange={(e) => setInvoiceObservation(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Opcional. Em branco usa o padrão da loja.
                </p>
              </div>

              {/* Error banner */}
              {returnError && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{returnError}</p>
                </div>
              )}
            </div>

            <DialogFooter className="pt-4 mt-2 border-t border-border flex-row justify-between sm:justify-between gap-2 sm:gap-0">
              <Button
                type="button"
                variant="ghost"
                onClick={handleModalClose}
                disabled={returnLoading}
                className="text-muted-foreground"
              >
                Fechar
              </Button>
              <Button
                type="button"
                onClick={handleConfirmClick}
                disabled={returnLoading}
                size="lg"
                className="gap-2 min-w-52"
              >
                {returnLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Confirmar Devolução
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* ── Photo lightbox ───────────────────────────────────────────────────── */}
      {expandedPhotoUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setExpandedPhotoUrl(null)}
        >
          <div
            className="relative max-w-3xl max-h-[90vh] w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setExpandedPhotoUrl(null)}
              className="absolute -top-10 right-0 flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
              Fechar
            </button>
            <img
              src={expandedPhotoUrl}
              alt="Foto do produto"
              className="w-full h-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* ── Partial return confirmation dialog ──────────────────────────────── */}
      <Dialog open={showPartialDialog} onOpenChange={setShowPartialDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex flex-col items-center gap-3 pb-1">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-yellow-500/10 border border-yellow-500/30">
                <AlertTriangle className="w-7 h-7 text-yellow-500" />
              </div>
              <DialogTitle className="text-center text-lg">
                Devolução Parcial
              </DialogTitle>
            </div>
            <DialogDescription className="text-center text-sm text-muted-foreground">
              Esta devolução possui itens pendentes. Revise os detalhes abaixo
              antes de confirmar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div className="rounded-xl border border-yellow-500/25 bg-yellow-500/8 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-500 shrink-0" />
                <p className="text-xs font-semibold text-yellow-400 uppercase tracking-wider">
                  Devolução parcial de becas
                </p>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Parte das becas ainda não será devolvida nesta operação.
              </p>
            </div>
            <p className="text-xs text-center text-muted-foreground pt-1">
              O contrato permanecerá em aberto. Você poderá completar a
              devolução depois.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPartialDialog(false)}
            >
              Voltar
            </Button>
            <Button
              type="button"
              className="gap-2"
              onClick={() => {
                setShowPartialDialog(false);
                void handleReturn();
              }}
            >
              <CheckCircle2 className="w-4 h-4" />
              Confirmar mesmo assim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
